/**
 * AIActivityPage
 *
 * Admin page for monitoring AI usage, costs, and performance.
 * Features date range selection, summary cards, and visualizations.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Brain,
  Loader2,
  RefreshCw,
  AlertCircle,
  DollarSign,
  Activity,
  Zap,
  Clock,
} from 'lucide-react';
import {
  useGetAIActivitySummary,
  useGetAIActivityByTaskType,
  useGetAIActivityByUser,
  useGetAIActivityTrend,
  useGetAIActivityByModel,
  AIActivitySummary,
  TaskTypeActivity,
  UserActivity,
  DailyStats,
  ModelActivity,
  formatCurrency,
  formatCompactNumber,
  formatNumber,
  formatProcessingTime,
} from '../hooks/useAdminApi';
import { DateRangePicker, SimpleBarChart } from '../components';
import { getLastNDays } from '../utils/dateHelpers';

interface DateRange {
  startDate: string;
  endDate: string;
}

export function AIActivityPage(): JSX.Element {
  const navigate = useNavigate();

  // Default to last 7 days
  const [dateRange, setDateRange] = useState<DateRange>(() => getLastNDays(7));

  // API hooks
  const { execute: fetchSummary, loading: loadingSummary, error: errorSummary } = useGetAIActivitySummary();
  const { execute: fetchByTaskType, loading: loadingTaskType } = useGetAIActivityByTaskType();
  const { execute: fetchByUser, loading: loadingUser } = useGetAIActivityByUser();
  const { execute: fetchTrend, loading: loadingTrend } = useGetAIActivityTrend();
  const { execute: fetchByModel, loading: loadingModel } = useGetAIActivityByModel();

  // Data state
  const [summary, setSummary] = useState<AIActivitySummary | null>(null);
  const [taskTypes, setTaskTypes] = useState<TaskTypeActivity[]>([]);
  const [users, setUsers] = useState<UserActivity[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [models, setModels] = useState<ModelActivity[]>([]);

  const loadAllData = useCallback(async () => {
    const requests = [
      fetchSummary(dateRange).then(setSummary).catch(console.error),
      fetchByTaskType({ ...dateRange, limit: 10 })
        .then((res) => setTaskTypes(res?.byTaskType ?? []))
        .catch(console.error),
      fetchByUser({ ...dateRange, limit: 10 })
        .then((res) => setUsers(res?.users ?? []))
        .catch(console.error),
      fetchTrend(dateRange)
        .then((res) => setDailyStats(res?.trend ?? []))
        .catch(console.error),
      fetchByModel(dateRange)
        .then((res) => setModels(res?.byModel ?? []))
        .catch(console.error),
    ];

    await Promise.allSettled(requests);
  }, [dateRange, fetchSummary, fetchByTaskType, fetchByUser, fetchTrend, fetchByModel]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const isLoading = loadingSummary || loadingTaskType || loadingUser || loadingTrend || loadingModel;
  const avgCostPerRequest = summary && summary.totalRequests > 0
    ? summary.totalCostUsd / summary.totalRequests
    : 0;

  // Calculate max daily requests for chart scaling
  const maxDailyRequests = Math.max(...dailyStats.map((d) => d.requests), 1);

  return (
    <div className="min-h-screen bg-airy">
      {/* Header */}
      <header className="bg-white border-b border-soft-neutral sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="btn-ghost btn-sm flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Admin
              </button>
              <div className="h-6 w-px bg-soft-neutral" />
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-evergreen" />
                <h1 className="text-xl font-semibold text-ink">AI Activity</h1>
              </div>
            </div>
            <button
              onClick={loadAllData}
              disabled={isLoading}
              className="btn-ghost btn-sm flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Date Range Picker */}
        <div className="card p-4 mb-6">
          <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
        </div>

        {/* Error State */}
        {errorSummary && (
          <div className="alert alert-error flex items-center gap-2 mb-6">
            <AlertCircle className="w-5 h-5" />
            {errorSummary}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-sm text-muted">Total Cost</p>
            </div>
            {loadingSummary ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted" />
            ) : (
              <p className="text-2xl font-semibold text-ink">
                {formatCurrency(summary?.totalCostUsd || 0)}
              </p>
            )}
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-sm text-muted">Total Requests</p>
            </div>
            {loadingSummary ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted" />
            ) : (
              <p className="text-2xl font-semibold text-ink">
                {formatNumber(summary?.totalRequests || 0)}
              </p>
            )}
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Zap className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-sm text-muted">Total Tokens</p>
            </div>
            {loadingSummary ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted" />
            ) : (
              <p className="text-2xl font-semibold text-ink">
                {formatCompactNumber(summary?.totalTokens || 0)}
              </p>
            )}
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <p className="text-sm text-muted">Avg Cost/Request</p>
            </div>
            {loadingSummary ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted" />
            ) : (
              <p className="text-2xl font-semibold text-ink">{formatCurrency(avgCostPerRequest)}</p>
            )}
          </div>
        </div>

        {/* Daily Trend Chart */}
        <div className="card p-4 mb-6">
          <h3 className="text-lg font-semibold text-ink mb-4">Daily Trend</h3>
          {loadingTrend ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted" />
            </div>
          ) : dailyStats.length === 0 ? (
            <p className="text-center text-muted py-8">No data for this period</p>
          ) : (
            <div className="space-y-1">
              {dailyStats.map((day) => {
                const percentage = (day.requests / maxDailyRequests) * 100;
                return (
                  <div key={day.date} className="flex items-center gap-3">
                    <div className="w-24 text-xs text-muted flex-shrink-0">
                      {new Date(day.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                    <div className="flex-1 h-6 bg-soft-neutral rounded overflow-hidden relative">
                      <div
                        className="h-full bg-evergreen/70 rounded transition-all duration-300"
                        style={{ width: `${Math.max(percentage, 2)}%` }}
                      />
                      {/* Cost overlay bar */}
                      <div
                        className="absolute top-0 left-0 h-full bg-green-400/50 rounded"
                        style={{
                          width: `${
                            (day.costUsd / Math.max(...dailyStats.map((d) => d.costUsd), 0.01)) * 100
                          }%`,
                          maxWidth: '100%',
                        }}
                      />
                    </div>
                    <div className="w-16 text-xs text-muted text-right flex-shrink-0">
                      {day.requests} req
                    </div>
                    <div className="w-16 text-xs text-green-600 text-right flex-shrink-0">
                      {formatCurrency(day.costUsd)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-4 mt-4 text-xs text-muted">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-evergreen/70 rounded" />
              <span>Requests</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-400/50 rounded" />
              <span>Cost</span>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* By Task Type */}
          <div className="card p-4">
            <h3 className="text-lg font-semibold text-ink mb-4">By Task Type</h3>
            {loadingTaskType ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted" />
              </div>
            ) : taskTypes.length === 0 ? (
              <p className="text-center text-muted py-8">No data for this period</p>
            ) : (
              <SimpleBarChart
                data={taskTypes.map((t) => ({
                  label: t.taskType,
                  value: t.estimatedCostUsd,
                  formattedValue: `${formatCurrency(t.estimatedCostUsd)} (${t.count} req)`,
                }))}
                maxLabelWidth="140px"
              />
            )}
          </div>

          {/* By User (Top 10) */}
          <div className="card p-4">
            <h3 className="text-lg font-semibold text-ink mb-4">Top Users by Cost</h3>
            {loadingUser ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted" />
              </div>
            ) : users.length === 0 ? (
              <p className="text-center text-muted py-8">No data for this period</p>
            ) : (
              <div className="space-y-2">
                {users.map((user, index) => (
                  <div key={user.userId} className="flex items-center gap-3">
                    <span className="w-6 text-sm text-muted">{index + 1}.</span>
                    <span className="flex-1 text-sm text-ink truncate" title={user.email || user.userId}>
                      {user.email || user.userId.slice(0, 12) + '...'}
                    </span>
                    <span className="text-sm text-muted">{user.totalRequests} req</span>
                    <span className="text-sm font-medium text-green-600 w-20 text-right">
                      {formatCurrency(user.totalCostUsd)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* By Model */}
        <div className="card p-4">
          <h3 className="text-lg font-semibold text-ink mb-4">By Model</h3>
          {loadingModel ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted" />
            </div>
          ) : models.length === 0 ? (
            <p className="text-center text-muted py-8">No data for this period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-soft-neutral">
                    <th className="text-left py-2 px-3 font-medium text-muted">Model</th>
                    <th className="text-right py-2 px-3 font-medium text-muted">Requests</th>
                    <th className="text-right py-2 px-3 font-medium text-muted">Tokens</th>
                    <th className="text-right py-2 px-3 font-medium text-muted">Cost</th>
                    <th className="text-right py-2 px-3 font-medium text-muted">Avg Time</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((model) => (
                    <tr key={model.modelId} className="border-b border-soft-neutral/50">
                      <td className="py-2 px-3 text-ink font-medium">{model.modelId}</td>
                      <td className="py-2 px-3 text-right text-muted">
                        {formatNumber(model.count)}
                      </td>
                      <td className="py-2 px-3 text-right text-muted">
                        {formatCompactNumber(model.totalTokens)}
                      </td>
                      <td className="py-2 px-3 text-right text-green-600 font-medium">
                        {formatCurrency(model.estimatedCostUsd)}
                      </td>
                      <td className="py-2 px-3 text-right text-muted">
                        {formatProcessingTime(model.avgProcessingTimeMs)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AIActivityPage;
